import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import {
  Badge,
  Box,
  Grid,
  IconButton,
  Paper,
  Theme,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { AvatarsAutocompleteInput, AvatarInputProps } from '../table';
import { TagInputProps } from '..';
import { useItemColors } from '../../utils/item.utils';
import {
  UseMutation,
  UseLazyQuery,
  UseQueryState,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { IconCircle, IconType } from './IconCircle';
import { ItemDate } from './ItemDateCollapse';

export interface CalendarItemProps {
  documentId?: string;
  data?: any;
  title: string;
  msgs?: number;
  cmts?: number;
  numChildren?: number;
  childrenPastDue?: number;
  childrenAtRisk?: number;
  statusCount?: { [key: string]: number };
  due_date?: Date;
  start_date?: Date;
  range?: boolean;
  complete?: boolean;
  onDateChange?: Function;
  onAssignmentChange?: Function;
  onItemClick?: Function;
  onMailClick?: Function;
  onChatClick?: Function;
  menuItems?: { tooltip: string; icon: JSX.Element; onClick: Function }[];
  selected?: boolean;
  highlighted?: boolean;
  showDetails?: boolean;
  icon?: 'task' | 'event' | 'coordination';
  icons?: IconType[];
  dates?: ItemDate[];
  edit?: Function;
  isLoading?: boolean;
  expandParent?: Function;
  disabled?: boolean;
  template?: boolean;
  tags?: TagInputProps;
  assignees?: AvatarInputProps;
  patchMutation?: UseMutation<any>;
  lazyQuery?: UseLazyQuery<any>;
  subscription?: UseQueryState<any>;
  subscriptionParams?: object;
  subscriptionTransform?: (values: any) => any;
  reloadParent?: Function;
  onStatusChange?: Function;
  statuses?: any[];
}

const sidebarwidth = '5px';
const borderwidth = '1px';
const ITEMSTATUS_COMPLETE = 3;
export const CalendarItem = forwardRef(
  (
    {
      documentId,
      data,
      title,
      msgs = 0,
      cmts = 0,
      numChildren = 0,
      childrenPastDue = 0,
      childrenAtRisk = 0,
      statusCount,
      due_date,
      start_date,
      complete = false,
      onDateChange = () => {},
      onAssignmentChange = () => {},
      onMailClick = () => {},
      onChatClick = () => {},
      menuItems,
      selected,
      highlighted = false,
      icon = 'task',
      icons,
      dates,
      showDetails = false,
      edit,
      isLoading,
      expandParent,
      disabled = false,
      template = false,
      assignees,
      tags,
      patchMutation,
      lazyQuery,
      subscription,
      subscriptionParams,
      subscriptionTransform = (values: any) => values,
      reloadParent = () => {},
      statuses,
      onStatusChange = () => {},
    }: CalendarItemProps,
    ref
  ) => {
    const [patch, patchResult] = (patchMutation && patchMutation()) || [
      () => {},
      {},
    ];
    const [reload, reloadResult] = (lazyQuery && lazyQuery()) || [() => {}];
    const { data: itemCache } =
      (subscription &&
        subscriptionParams &&
        subscription(subscriptionParams)) ||
      {};

    const triggerReload = async () => {
      if (subscriptionParams) {
        await reload(subscriptionParams);
      }
    };

    const theme = useTheme();
    const downSm = useMediaQuery(theme.breakpoints.down('sm'));
    const upMd = useMediaQuery(theme.breakpoints.up('md'));
    const upLg = useMediaQuery(theme.breakpoints.up('lg'));

    const scrollRef = useRef<HTMLElement>(null);
    const scrollToRef = () => {
      if (scrollRef && scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };
    const iconRef = useRef<HTMLDivElement>(null);
    const dateChipRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      expandParent: () => {
        expandParent && expandParent();
      },
      triggerReload: () => {
        triggerReload();
      },
      scrollRef: scrollRef,
    }));

    useEffect(() => {
      if (selected) {
        scrollToRef();
      }
    }, [selected]);
    const [itemValues, setItemValues] = useState<any>({
      title,
      start_date: dates?.[0]?.start || start_date,
      due_date: dates?.[0]?.end || due_date,
      append: dates?.[0]?.append,
      dateDisabled: dates?.[0]?.disabled,
      onDateChange: dates?.[0]?.onDateChange,
      complete,
      assignees: assignees?.data,
      status: data?.status_r?.name,
    });

    useEffect(() => {
      const item: any = subscriptionTransform(itemCache);
      if (item?.id) {
        setItemValues((pv: any) => ({
          ...pv,
          title: item?.title,
          start_date: item?.dates?.[0]?.start || item.start_date,
          due_date: item?.dates?.[0]?.end || item.due_date,
          append: item?.dates?.[0]?.append,
          dateDisabled: item?.dates?.[0]?.disabled,
          onDateChange: item?.dates?.[0]?.onDateChange,
          complete: item?.status_r?.id
            ? item?.status_r?.id === ITEMSTATUS_COMPLETE
            : pv?.complete,
          status: item?.status_r?.name || pv?.status,
        }));
      }
    }, [itemCache]);

    useEffect(() => {
      const item: any = subscriptionTransform(patchResult?.data);
      if (item) {
        setItemValues((pv: any) => ({
          ...pv,
          title: item?.title || pv.title,
          start_date: item?.dates?.[0]?.start || item.start_date,
          due_date: item?.dates?.[0]?.end || item.due_date,
          append: item?.dates?.[0]?.append,
          dateDisabled: item?.dates?.[0]?.disabled,
          onDateChange: item?.dates?.[0]?.onDateChange,
          complete: item?.status_r?.id
            ? item?.status_r?.id === ITEMSTATUS_COMPLETE
            : pv?.complete,
          status: item?.status_r?.name || pv?.status,
        }));
      }
    }, [patchResult]);

    useEffect(() => {
      setItemValues((pv: any) => ({
        ...pv,
        complete,
        status: data?.status_r?.name || pv?.status?.label,
        // assignees: assignees?.data,
      }));
    }, [complete, data?.masked]);
    const [menu, setMenu] = useState<boolean>(false);
    const containerRef = useRef(null);

    const {
      item: {
        border: borderColor,
        background: backgroundColor,
        font: fontColor,
      },
    } = useItemColors({
      date: itemValues?.due_date || itemValues?.start_date,
      complete: itemValues?.complete,
    });

    const [hover, setHover] = useState<boolean>(false);
    const [iconHover, setIconHover] = useState<boolean>(false);
    const [prevValue, setPrevValue] = useState<string>(title);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [iconAnchorEl, setIconAnchorEl] =
      useState<HTMLDivElement | null>(null);
    const [dateExpand, setDateExpand] = useState<boolean>(false);

    return (
      <Paper
        id={documentId}
        ref={ref as any}
        elevation={hover ? 10 : 0}
        onMouseEnter={() => setHover(true && !disabled)}
        onMouseLeave={() => setHover(false)}
        sx={{
          borderLeft: `${sidebarwidth} solid ${borderColor}`,
          borderTop: `${borderwidth} solid ${borderColor}`,
          borderBottom: `${borderwidth} solid ${borderColor}`,
          borderRight: `${borderwidth} solid ${borderColor}`,
          backgroundColor: selected ? 'red' : backgroundColor,
          zIndex:
            selected || hover ? (theme: Theme) => theme.zIndex.drawer - 10 : 0,
        }}
      >
        <span ref={scrollRef}></span>
        <Grid
          container
          alignContent={'center'}
          spacing={0}
          wrap={hover ? 'wrap' : 'nowrap'}
        >
          <Grid
            item
            container
            spacing={0}
            sx={upMd ? { width: 'fit-content' } : { flex: 1 }}
            order={1}
            wrap={'nowrap'}
          >
            {itemValues.complete && (
              <Grid item>
                <IconButton
                  size={'small'}
                  edge="end"
                  disabled={true}
                  color="primary"
                  onClick={async event => {
                    event.stopPropagation();
                    event.preventDefault();
                    onStatusChange(
                      itemValues.complete
                        ? { id: 2, name: 'In Progress' }
                        : { id: 3, name: 'Complete' },
                      () =>
                        setItemValues((pv: any) => ({
                          ...pv,
                          complete: !pv.complete,
                          status: pv.complete ? 'In Progress' : 'Complete',
                        }))
                    );
                  }}
                  sx={
                    {
                      // zoom: '75%',
                      // '&:hover': {
                      //   zoom: '100%',
                      // },
                    }
                  }
                >
                  {itemValues.complete ? (
                    <CheckBox />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </IconButton>
              </Grid>
            )}
            {icons && (
              <Grid item alignSelf={'center'}>
                <Box
                  sx={
                    {
                      // zoom: '75%',
                    }
                  }
                >
                  <IconCircle icons={icons} />
                </Box>
              </Grid>
            )}
          </Grid>

          <Grid
            item
            {...(!upMd ? { xs: 12 } : {})}
            container
            spacing={0}
            sx={{
              alignItems: 'center',
              ...(upMd ? { flex: 1 } : { ml: 0, pb: 1, pr: 1 }),
            }}
            order={upMd ? 2 : 3}
            wrap={'nowrap'}
          >
            <Grid
              item
              container
              spacing={0}
              sx={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}
              wrap={'nowrap'}
            >
              <Grid item>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    boxSizing: 'border-box',
                    ml: 0.8,
                    whiteSpace: hover ? 'wrap' : 'nowrap',
                  }}
                >
                  {itemValues.title}&nbsp;
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            {...(!upLg ? { xs: 12 } : {})}
            container
            spacing={0}
            order={upLg ? 3 : 4}
            sx={{
              alignItems: 'center',
              ...(upLg ? { width: 'fit-content' } : { ml: 0, pb: 1, pr: 1 }),
            }}
            wrap={'nowrap'}
          >
            <Grid
              item
              {...(downSm ? { xs: 12 } : {})}
              sx={{
                ml: 1,
                // zoom: '75%'
              }}
            >
              <AvatarsAutocompleteInput
                {...{
                  ...assignees,
                  disabled: true || template,
                  hover: false && hover && !template,
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  }
);
